import { Table, Select, Typography, Button, Space } from "@mimber/ui-web"
import type { ColumnsType } from "@mimber/ui-web"
import type { ColumnMappingStepProps } from "./ColumnMappingStep.types"

const { Paragraph, Text } = Typography

const DO_NOT_IMPORT = ""

interface MappingRow {
  key: string
  csvHeader: string
}

export const ColumnMappingStep: React.FunctionComponent<
  ColumnMappingStepProps
> = ({ csvHeaders, fieldDefinitions, mapping, onChange, onBack, onNext }) => {
  const rows: MappingRow[] = csvHeaders.map((csvHeader) => ({
    key: csvHeader,
    csvHeader,
  }))

  const mappedFieldKeys = new Set(Object.values(mapping).filter(Boolean))

  const requiredFieldKeys = fieldDefinitions
    .filter((field) => field.required)
    .map((field) => field.key)
  const allRequiredMapped = requiredFieldKeys.every((key) =>
    mappedFieldKeys.has(key)
  )

  const handleSelect = (csvHeader: string, fieldKey: string) => {
    const next = { ...mapping }
    if (fieldKey === DO_NOT_IMPORT) {
      delete next[csvHeader]
    } else {
      // Only one CSV column may map to a given target field
      for (const key of Object.keys(next)) {
        if (next[key] === fieldKey) delete next[key]
      }
      next[csvHeader] = fieldKey
    }
    onChange(next)
  }

  const columns: ColumnsType<MappingRow> = [
    {
      title: "CSV column",
      dataIndex: "csvHeader",
      render: (value: string) => <Text strong>{value}</Text>,
    },
    {
      title: "Maps to",
      render: (_, record) => (
        <Select
          style={{ width: 220 }}
          value={mapping[record.csvHeader] ?? DO_NOT_IMPORT}
          onChange={(value) => handleSelect(record.csvHeader, value)}
          options={[
            { value: DO_NOT_IMPORT, label: "Do not import" },
            ...fieldDefinitions.map((field) => ({
              value: field.key,
              label: field.required ? `${field.label} (required)` : field.label,
              disabled:
                mappedFieldKeys.has(field.key) &&
                mapping[record.csvHeader] !== field.key,
            })),
          ]}
        />
      ),
    },
  ]

  return (
    <div>
      <Paragraph type="secondary">
        Match each column from your file to a field. Columns left as "Do not
        import" are ignored.
      </Paragraph>
      <Table
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="small"
      />
      <div style={{ marginTop: 24 }}>
        <Space>
          <Button onClick={onBack} data-testid="column-mapping-back">
            Back
          </Button>
          <Button
            type="primary"
            onClick={onNext}
            disabled={!allRequiredMapped}
            data-testid="column-mapping-next"
          >
            Next
          </Button>
        </Space>
      </div>
    </div>
  )
}
